import { Stack, Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';

import { gql } from '@graphql/__generated__';
import type {
  LightLotCardInfo_LotFragment,
  LotCardInfo_LotFragment,
} from '@graphql/__generated__/graphql';
import { Tag } from '@modules/_shared/components/Tag';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  formatAppraisalDiff,
  getListingPrices,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import { type Translate, useLocale } from '@shared/hooks/locale';

export const LOT_CARD_INFO_FRAGMENT = gql(/* GraphQL */ `
  fragment LotCardInfo_lot on lots {
    id
    ...LightLotCardInfo_lot
    enquiries_aggregate {
      aggregate {
        count
      }
    }
    offers_aggregate {
      aggregate {
        count
      }
    }
  }
`);

export const LIGHT_LOT_CARD_INFO_FRAGMENT = gql(/* GraphQL */ `
  fragment LightLotCardInfo_lot on lots {
    id
    title
    status
    hide_price_on_portals
    shared_on_mls_at
    property {
      id
      route
      street_number
      postcode
      locality
    }
    ...ListingPrice
  }
`);

type Props =
  | {
      lot: LotCardInfo_LotFragment;
      showTitle?: boolean;
      showInlineLabels: true;
    }
  | {
      lot: LightLotCardInfo_LotFragment;
      showTitle?: boolean;
      showInlineLabels?: false;
    };

export const getLotStatusTagVariant = (status: string) => {
  switch (status) {
    case 'available':
    case 'available_coming_soon':
    case 'available_off_market':
      return 'info';
    case 'reserved':
      return 'warning';
    case 'won':
      return 'success';
    case 'lost_sold_someone':
    case 'lost_sold_owner':
    case 'abandoned':
      return 'error';
    case 'rejected':
      return 'error';
    case 'pending_review':
    case 'draft':
    case 'paused':
    default:
      return 'neutral';
  }
};

export const getLotStatusLabel = (status: string, t: Translate) => {
  switch (status) {
    case 'available':
      return t('available');
    case 'available_coming_soon':
      return t('availableComingSoon');
    case 'available_off_market':
      return t('availableOffMarket');
    case 'reserved':
      return t('reserved');
    case 'won':
      return t('won');
    case 'lost_sold_someone':
      return t('lostSoldBySomeone');
    case 'lost_sold_owner':
      return t('lostSoldByOwner');
    case 'abandoned':
      return t('lostAbandoned');
    case 'draft':
      return t('draft');
    case 'paused':
      return t('paused');
    case 'pending_review':
      return t('Pending review');
    case 'rejected':
      return t('Rejected');
    default:
      return status;
  }
};

export const LotCardInfo = (props: Props) => {
  const { t, locale } = useLocale();
  const { lot } = props;

  const prices = getListingPrices(lot, t, locale);

  const statusLabel = getLotStatusLabel(lot.status, t);

  return (
    <Stack
      direction="column"
      sx={{
        flexGrow: 1,
      }}
    >
      <Typography variant="caption" noWrap>
        {formatAddress(lot.property, ', ')}
      </Typography>
      {props.showTitle != null && (
        <Typography variant="body1" noWrap>
          {lot.title != null && lot.title !== '' ? lot.title : '-'}
        </Typography>
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 700,
          }}
        >
          {prices.primary != null ? (
            lot.hide_price_on_portals === true ? (
              <s>{prices.primary}</s>
            ) : (
              prices.primary
            )
          ) : (
            t('On request')
          )}
        </Typography>

        {prices.appraisalDiff != null && (
          <Typography
            variant="subtitle2"
            color={prices.appraisalDiff > 0 ? green[700] : red[700]}
            sx={{
              pl: 1,
              fontWeight: 700,
            }}
          >
            {formatAppraisalDiff(prices.appraisalDiff, locale)}
          </Typography>
        )}
      </Stack>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
        }}
      >
        {prices.secondary ?? '-'}
      </Typography>
      {props.showInlineLabels === true && (
        <Stack
          direction="row"
          sx={{
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Tag status={getLotStatusTagVariant(lot.status)}>{statusLabel}</Tag>
          {lot.shared_on_mls_at != null && <Tag status="info">{t('MLS')}</Tag>}
          {(props.lot.enquiries_aggregate.aggregate?.count ?? 0) > 0 && (
            <Tag status="neutral">
              {t('enquiriesCount', {
                count: props.lot.enquiries_aggregate.aggregate?.count ?? 0,
              })}
            </Tag>
          )}
          {(props.lot.offers_aggregate.aggregate?.count ?? 0) > 0 && (
            <Tag status="neutral">
              {t('offersCount', {
                count: props.lot.offers_aggregate.aggregate?.count ?? 0,
              })}
            </Tag>
          )}
        </Stack>
      )}
    </Stack>
  );
};
